import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { waitForWishlist } from '../../../../utils/wishlist';
import { initWishlist } from '../../../../snippets/customer.wishlist';
import { handleLoginCreate } from '../../../../utils/signInOutUp/AuthenticationHelper';
import useMobileVerify from './useMobileVerify';
import { MobileVerifyDataType, useMobileVerifyContext } from '../context/MobileVerifyContext';
import { trackLoginStart } from '../../../../hooks/useLoginTracking/useLoginTracking';
import { baseEventData } from '../../../../hooks/useLoginTracking/constants';
import { useVerifyLayout } from './useVerifyLayout/useVerifyLayout';
import { checkIsPerksRedirectSetAndNavigate } from '../../perksInfo/utils';
import { WISHLIST_SESSION_ITEM_KEY } from '../../../../contexts/ShopifyCustomerProvider';
import { useSubscribe } from '@jbhi-fi/components/src/hooks/useKlaviyoSubscribe';

const useLoginPage = (): {
  onVerifyMobileResponse: (response: MobileVerifyDataType) => void;
} => {
  const [shouldStartVerifying, setShouldStartVerifying] = useState<boolean>(false);
  const hasInitiallyRedirected = useRef<boolean>(false);
  const history = useHistory();
  const { themeContext } = useVerifyLayout();
  const { setMobileVerifyData, data: mobileVerifyData } = useMobileVerifyContext();
  const { onSubscribe } = useSubscribe();

  const handleWishlistItem = async (wishlistItem: string): Promise<void> => {
    initWishlist();
    await waitForWishlist();
    const wishlistItemParts = wishlistItem.split(':');
    try {
      await window.theme.wishlist.addDirect(...wishlistItemParts);
      window.sessionStorage.setItem(WISHLIST_SESSION_ITEM_KEY, wishlistItemParts[0]);
    } catch (error) {
      console.error(error);
    }
  };

  const checkB2cServerErrors = () => {
    const hash = new URL(window.location.href).hash;
    if (hash.includes('alert')) {
      const hashes = hash.split('&');
      hashes.forEach(pair => {
        if (pair.includes('=')) {
          const keyValue = pair.split('=');
          if (keyValue[0] === 'alert') {
            let alert = keyValue[1];
            if (alert === 'AADB2C90085') alert = 'B2C Secrets Expired!';
            else if (alert === 'server_error') alert = 'B2C Internal Server Error!';
            else
              alert = `B2C Error: ${alert} (https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes)`;
            console.error(`Login error: '${alert}'`);
          }
        }
      });
    }
  };

  const getReturnUrl = (): string => {
    const query = new URL(window.location.href).searchParams;
    if (query.get('pageTheme') === 'perks') {
      return `/account/login?${query && query.toString()}`;
    }

    if (query.has('return_url')) {
      /*  When shopify redirects a non-logged in user from /account/xxx to /account/login?return_url=/account/xxx
            the return_url parameter is NOT encoded, so new URLSearchParams doesn't work.  */
      return decodeURIComponent(/return_url=.*/gi.exec(window.location.href)[0].replace(/return_url=/gi, ''));
    }

    return new URL(window.location.href).searchParams.get('returnUrl');
  };

  const handleReturnUrl = async () => {
    const query = new URL(window.location.href).searchParams;
    if (query.has('wishlistItem')) {
      await handleWishlistItem(query.get('wishlistItem'));
    }
    window.location.assign(getReturnUrl());
  };

  const handleNotLoggedIn = () => {
    trackLoginStart({ baseEventData: baseEventData.default });
    handleLoginCreate(getReturnUrl());
  };

  const handleInitialRedirect = async () => {
    checkB2cServerErrors();
    const query = new URL(window.location.href).searchParams;

    if (query.has('newUser') && window.Shopify?.Customer?.email) {
      await onSubscribe({ email: window.Shopify?.Customer?.email, listId: window.Jbhifi?.klaviyoMailingListId });
    }

    if (!window.Jbhifi.customerDetails?.isLoggedIn) {
      return handleNotLoggedIn();
    }
    if (query.has('returnUrl') ?? query.has('return_url')) {
      return await handleReturnUrl();
    }
    if (themeContext.shouldSkipPhoneVerification) {
      return setMobileVerifyData({ isFinished: true });
    }
    setShouldStartVerifying(true);
  };

  const onVerifyMobileResponse = (response: MobileVerifyDataType) => {
    if (
      response.isSuccess &&
      (!response.loginMobileVerifySkipped || themeContext?.ignoreSkipVerifyPreference) &&
      !response.mobileNumber &&
      !response.currentMobileNumber &&
      !response.customerId &&
      !response.isFinished
    ) {
      const query = new URL(window.location.href).searchParams;
      if (query.get('perksReInstated') === 'true') {
        window.location.href = '/pages/perks';
      } else {
        history.push(`/link-your-in-store-receipts`);
      }
    } else if (response.isFinished) {
      if (!checkIsPerksRedirectSetAndNavigate()) {
        history.push('/');
      }
    } else if (response.isTooManyAttempts) {
      history.push('/oops-youve-tried-too-many-times');
    } else if (response.isAmbiguous) {
      history.push('/mobile-number-already-verified');
    } else if (response.mobileNumber && response.customerId) {
      history.push('/enter-verification-code');
    } else if (!themeContext.skipBirthdayPrompt && !themeContext.birthMonthExists) {
      setMobileVerifyData({ ...response, isFinished: true });
    } else {
      setMobileVerifyData({ ...response, isFinished: true });
      if (!checkIsPerksRedirectSetAndNavigate()) {
        history.push('/');
      }
    }
  };

  useEffect(() => {
    if (themeContext.skipBirthdayPrompt && mobileVerifyData.isFinished) {
      if (!checkIsPerksRedirectSetAndNavigate()) {
        history.push('/');
      }
    }
    if (themeContext && !hasInitiallyRedirected.current) {
      hasInitiallyRedirected.current = true;
      handleInitialRedirect();
    }
  }, [themeContext]);

  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const context = useMobileVerify(onVerifyMobileResponse, shouldStartVerifying);

  return { ...context, onVerifyMobileResponse };
};

export default useLoginPage;
